import React, {useEffect, useMemo} from 'react';
import {View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react';

import Text from '../../components/ui-controls/Text';
import {useSearchDispatch} from '../../context/SearchContext';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';

import RangeProductListScreen from './RangeProductListScreen';

const SubDepartmentScreen = observer(
  ({route}: RouteNavigatorScreenProps<AppRoute.SUB_DEPARTMENT>) => {
    const {rangeStore, filterStore} = useStores();
    const searchDispatch = useSearchDispatch();

    // Prepare department/subdepartment from route params
    // Guard against undefined params (can happen during deep link parsing before data loads)
    const {department: deptParam, subDepartment: subDeptParam} = route.params ?? {};

    const {department, subDepartment} = useMemo(() => {
      if (subDeptParam?.id === 'view-all') {
        return {
          department: {...deptParam, name: 'View All'},
          subDepartment: undefined,
        };
      }
      return {department: deptParam, subDepartment: subDeptParam};
    }, [deptParam, subDeptParam]);

    // Store mutations MUST be in useEffect, not during render
    useEffect(() => {
      rangeStore.selectDepartment(department);
      filterStore.clearSortAndFilters();
      if (subDepartment) {
        rangeStore.selectSubDepartment(subDepartment);
      }
      // Scope SearchContext to the active sub-department (or parent for "View All")
      const activeDeptId = subDepartment?.id ?? department?.id;
      if (activeDeptId) {
        filterStore.loadFilterOptions();
        searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: activeDeptId});
      }
    }, [rangeStore, filterStore, department, subDepartment, searchDispatch]);

    // Re-establish department scope when this screen regains focus.
    // SearchScreen's CLEAR_ALL resets departmentId to null in the shared
    // SearchContext.  The mount useEffect above does NOT re-fire on
    // back-navigation (same route params).  SET_DEPARTMENT preserves
    // user-applied filters unlike SCOPE_TO_DEPARTMENT.
    useFocusEffect(
      React.useCallback(() => {
        const activeDeptId = subDepartment?.id ?? department?.id;
        if (activeDeptId) {
          searchDispatch({type: 'SET_DEPARTMENT', departmentId: activeDeptId});
        }
      }, [department?.id, subDepartment?.id, searchDispatch]),
    );

    if (!department || (department.name !== 'View All' && !subDepartment)) {
      return (
        <View>
          <Text>Category/SubCategory not found</Text>
        </View>
      );
    }

    // TODO: This depends on  rangeStore.selected... 🙄
    return <RangeProductListScreen />;
  },
);

export default SubDepartmentScreen;
